/**
 * ShareSet merged page: tab switching between V1 (shareset-ver1) and V2 (shareset-ver2).
 * Each panel has its own [data-sm-product-scope]; the existing product-main JS
 * inits per-form against that scope so swatch/gallery/price work independently.
 *
 * This file only handles tab clicks + URL sync.
 */
(function () {
  const root = document.querySelector('[data-share-set]');
  if (!root) return;

  const panels = root.querySelectorAll('[data-share-panel]');
  if (panels.length < 2) return;

  const v1Url = root.dataset.v1Url || '/products/shareset-ver1';
  const v2Url = root.dataset.v2Url || '/products/shareset-ver2';

  // Read color-swatch selections (positions 1 + 2) from a panel.
  // Returns { 1: 'ホワイト', 2: 'ブラック' } — only color options, not セット数.
  function readColorSelections(panel) {
    const selections = {};
    if (!panel) return selections;
    // Color swatches use .sm-pd-card__swatches; non-color (セット数) uses .sm-pd-card__pills.
    panel.querySelectorAll('.sm-pd-card__swatches [data-sm-swatch].is-selected').forEach((btn) => {
      const pos = btn.dataset.optionPosition;
      const val = btn.dataset.optionValue;
      if (pos && val) selections[pos] = val;
    });
    return selections;
  }

  // Apply color selections to a panel by clicking the matching swatch buttons.
  // Reuses the existing per-panel swatch click handler (in section-sm-product-main.js),
  // which updates variant id, price, gallery, and option labels.
  function applyColorSelections(panel, selections) {
    if (!panel) return;
    Object.keys(selections).forEach((pos) => {
      const val = selections[pos];
      const target = panel.querySelector(
        '.sm-pd-card__swatches [data-sm-swatch][data-option-position="' + pos +
        '"][data-option-value="' + CSS.escape(val) + '"]'
      );
      // Skip if already selected (avoid redundant click) or value not present in target product.
      if (!target || target.classList.contains('is-selected')) return;
      target.click();
    });
  }

  function setActive(key, options) {
    const opts = options || {};
    if (key !== 'v1' && key !== 'v2') return;

    // Capture color selections from current panel before swapping (we need
    // these regardless of order — the source panel is about to be hidden).
    const prevKey = root.dataset.activePanel;
    const fromPanel = prevKey ? root.querySelector('[data-share-panel="' + prevKey + '"]') : null;
    const carriedSelections = (!opts.skipColorSync && fromPanel && prevKey !== key)
      ? readColorSelections(fromPanel)
      : null;

    root.dataset.activePanel = key;

    // Swap visibility FIRST so the target panel has layout for gallery scroll.
    panels.forEach((panel) => {
      const match = panel.dataset.sharePanel === key;
      if (match) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Now sync colors into the visible target panel — its gallery scroll,
    // variant id, price update will work because the panel has layout.
    if (carriedSelections) {
      const toPanel = root.querySelector('[data-share-panel="' + key + '"]');
      applyColorSelections(toPanel, carriedSelections);
    }

    // Sync pressed state across all tab buttons (both panels have their own tabs).
    root.querySelectorAll('[data-share-set-tab]').forEach((btn) => {
      const target = btn.dataset.shareSetTab;
      const selected = target === key;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    // Update URL without reload, preserving the panel's own ?variant param.
    if (!opts.skipHistory) {
      const activePanel = root.querySelector('[data-share-panel="' + key + '"]');
      const idInput = activePanel && activePanel.querySelector('[data-sm-variant-id]');
      const baseUrl = key === 'v1' ? v1Url : v2Url;
      const url = new URL(baseUrl, window.location.origin);
      // Carry over the existing query params (e.g. ?view=share-set for dev
      // preview, or other tracking params) but drop variant — it belongs
      // to the previous product.
      const current = new URL(window.location.href);
      current.searchParams.forEach((value, name) => {
        if (name === 'variant') return;
        url.searchParams.set(name, value);
      });
      if (idInput && idInput.value) {
        url.searchParams.set('variant', idInput.value);
      }
      history.pushState({ shareSet: key }, '', url.toString());
    }

    // Trigger a resize so any layout-dependent JS in the newly visible panel
    // (e.g. gallery dot indicator width) recomputes.
    window.dispatchEvent(new Event('resize'));
  }

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-share-set-tab]');
    if (!btn || !root.contains(btn)) return;
    const target = btn.dataset.shareSetTab;
    if (!target || target === root.dataset.activePanel) return;
    e.preventDefault();
    setActive(target);
  });

  window.addEventListener('popstate', (e) => {
    const state = e.state;
    if (state && (state.shareSet === 'v1' || state.shareSet === 'v2')) {
      setActive(state.shareSet, { skipHistory: true });
      return;
    }
    // Fallback: infer from URL path
    const path = window.location.pathname;
    if (path.indexOf('shareset-ver2') !== -1) {
      setActive('v2', { skipHistory: true });
    } else if (path.indexOf('shareset-ver1') !== -1) {
      setActive('v1', { skipHistory: true });
    }
  });

  // Seed initial history state so back/forward returns here predictably.
  if (!history.state || !history.state.shareSet) {
    history.replaceState({ shareSet: root.dataset.activePanel }, '');
  }
})();
